import { ReactNode, useRef } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { Flex, FlexColumn } from '../Flex';

const StepsWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.common.gray8};
`;

const Step = styled(Flex)<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  width: 100%;
  gap: 24px;
  padding: 0 24px;
  height: 72px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.palette.common.gray9 : theme.palette.common.white};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.fontWeightBold : 400};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  :hover {
    background: ${({ theme, $disabled }) =>
      !$disabled && theme.palette.common.gray10};
  }
`;

const StepLeft = styled(Flex)`
  gap: 24px;
  align-items: center;
`;

const StepText = styled(Typography)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-weight: inherit;
  white-space: nowrap;
  color: ${({ theme, $selected, $stepOver }) =>
    $selected
      ? theme.palette.secondary.main
      : $stepOver
      ? theme.palette.common.black
      : theme.palette.common.slate};
`;

const StepSubText = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const StepDetail = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const StepIndex = styled(StepText)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-size: 24px;
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.secondary.main : theme.palette.common.slate};
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.common.gray8};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

type StepBoxProps = {
  index: number;
  currentStep: number;
  title: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

const StepBox = ({
  index,
  currentStep,
  title,
  description,
  icon,
  disabled,
  onClick,
}: StepBoxProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const stepNumber = index + 1; // Convert to 1-indexed for display

  return (
    <Step
      ref={ref}
      $selected={index === currentStep}
      $disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <StepLeft>
          <StepIndex
            $selected={index === currentStep}
            $stepOver={index < currentStep}
          >
            {stepNumber}
          </StepIndex>
          <FlexColumn>
            <StepText
              $selected={index === currentStep}
              $stepOver={index < currentStep}
            >
              {title}
            </StepText>
            {description && (
              <StepDetail>
                {icon && icon}
                <StepSubText noWrap>{description}</StepSubText>
              </StepDetail>
            )}
          </FlexColumn>
        </StepLeft>
      </Flex>
    </Step>
  );
};

type StepInfo = {
  title: string;
  description?: string;
  icon?: ReactNode;
};

type Props = {
  steps: StepInfo[];
  currentStep: number;
  onStepClick: (index: number) => void;
};

const WizardStepper = ({ steps, currentStep, onStepClick }: Props) => (
  <StepsWrapper divider={<Divider />}>
    {steps.map((step, index) => (
      <StepBox
        key={`wizard-step-${step.title}`}
        index={index}
        currentStep={currentStep}
        title={step.title}
        description={step.description}
        icon={step.icon}
        disabled={index > currentStep}
        onClick={() => onStepClick(index)}
      />
    ))}
  </StepsWrapper>
);

export default WizardStepper;
